// src/constants/apiEndpoints.ts
/**
 * ✅ API 엔드포인트 상수화
 * - 유지보수성 향상: URL 변경 시 한 곳만 수정
 * - 타입 안전성: 오타 방지
 * - 백엔드 URL과의 일관성 유지
 */

export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    SIGNUP: "/auth/signup/",
    LOGIN: "/auth/login/",
    LOGOUT: "/auth/logout/",
    REFRESH: "/token/refresh/",
    PROFILE: "/auth/profile/",
    CHANGE_PASSWORD: "/auth/change-password/",
    PASSWORD_RESET: "/auth/password-reset/",
    PASSWORD_RESET_CONFIRM: "/auth/password-reset-confirm/",
    DELETE_ACCOUNT: "/auth/delete-account/",
    FIND_USERNAME: "/auth/find-username/",
  },

  // Reports
  REPORTS: {
    EXAM_UPLOAD: "/reports/exam-upload/",
    EXAM_UPLOAD_ASYNC: "/reports/exam-upload-async/",
    TASK_STATUS: (taskId: string) => `/reports/task-status/${taskId}/`,
    TASK_CANCEL: (taskId: string) => `/reports/task-cancel/${taskId}/`,

    EXAM_ANALYSES: "/reports/exam-analyses/",
    EXAM_ANALYSIS_DETAIL: (id: number) => `/reports/exam-analyses/${id}/`,
    EXAM_ANALYSIS_PDF: (id: number) => `/reports/analyses/${id}/pdf/`,

    STUDENT_ANALYSES: (studentId: number) => `/reports/students/${studentId}/analyses/`,

    REPORTS_LIST: "/reports/reports/",
    REPORT_DETAIL: (id: number) => `/reports/reports/${id}/`,
    REPORT_REVIEW: (id: number) => `/reports/reports/${id}/review/`,
    REPORT_DETAILS: (id: number) => `/reports/reports/${id}/details/`,
    REPORT_SEND: (id: number) => `/reports/reports/${id}/send/`,

    DISPATCHES: "/reports/dispatches/",
    DISPATCH_DETAIL: (id: number) => `/reports/dispatches/${id}/`,

    DASHBOARD_STATS: "/reports/dashboard-stats/",

    // 단원별 정답률 (QuestionResult 집계)
    CURRICULUM_STATS_STUDENT: (studentId: number) =>
      `/reports/curriculum-stats/student/${studentId}/`,
    CURRICULUM_STATS_MY: "/reports/curriculum-stats/my/",
  },

  // Students
  STUDENTS: {
    BASE: "/students/",
    LIST: "/students/",
    DETAIL: (id: number) => `/students/${id}/`,
    ME: "/students/me/",
    MY_QUESTS: "/students/me/quests/",
    BULK_IMPORT: "/students/bulk-import/",
    CHECK_DUPLICATE: "/students/check-duplicate/",
    LINK_TEACHER: (id: number) => `/students/${id}/link-teacher/`,
    RESET_PASSWORD: (id: number) => `/students/${id}/reset-password/`,
  },

  // Web Push (PWA 푸시 알림)
  PUSH: {
    VAPID_PUBLIC_KEY: "/reports/push/vapid-public-key/",
    SUBSCRIBE: "/reports/push/subscribe/",
  },

  // FCM (네이티브 앱 푸시 알림)
  FCM: {
    REGISTER: "/reports/fcm/register/",
  },

  // CPI (Competence Performance Index)
  CPI: {
    MY: "/reports/cpi/my/",
    BRANCH: "/reports/cpi/branch/",
    STUDENT: (studentId: number) => `/reports/cpi/student/${studentId}/`,
    CHILD: (studentId: number) => `/reports/cpi/child/${studentId}/`,
    RECALCULATE: (studentId: number) => `/reports/cpi/student/${studentId}/recalculate/`,
    LMI_ASSESSMENT: (studentId: number) => `/reports/lmi-assessment/${studentId}/`,
    LMI_SURVEY_SEND: (studentId: number) => `/reports/lmi-survey/${studentId}/send/`,
    LMI_SURVEY_MINE: "/reports/lmi-survey/mine/",
    LMI_SURVEY_SUBMIT: "/reports/lmi-survey/mine/submit/",
  },

  // 학생 개인 수업 일정 (요일별 관리)
  DAY_SCHEDULES: {
    LIST:   (day?: number) => day != null ? `/students/day-schedules/?day_of_week=${day}` : '/students/day-schedules/',
    DETAIL: (id: number) => `/students/day-schedules/${id}/`,
  },

  // 학부모 전용
  PARENT: {
    MY_CHILDREN: "/students/my-children/",
    REPORTS: (studentId: number) => `/reports/parent/reports/?student_id=${studentId}`,
    REPORT_DETAIL: (pk: number) => `/reports/parent/reports/${pk}/`,
    STUDY_CALENDAR: (studentId: number, year: number, month: number) =>
      `/study/calendar/?student_id=${studentId}&year=${year}&month=${month}`,
    STUDY_TODAY: (studentId: number) => `/study/today/?student_id=${studentId}`,
    STUDENT_ANALYSES: (studentId: number) => `/reports/students/${studentId}/analyses/`,
  },

  // 반 배정 시스템
  GROUPS: {
    LIST:            '/groups/',
    DETAIL:          (id: number) => `/groups/${id}/`,
    ADD_STUDENTS:    (id: number) => `/groups/${id}/add_students/`,
    REMOVE_STUDENTS: (id: number) => `/groups/${id}/remove_students/`,
  },

  // CP (Competence Points) — 교사 행동 체크
  CP: {
    MY:                   '/cp/my/',
    MY_HISTORY:           '/cp/my/history/',
    LOG_EVENT:            '/cp/events/',
    CANCEL_EVENT:         (id: number) => `/cp/events/${id}/`,
    STUDENT_SUMMARY:      (studentId: number) => `/cp/student/${studentId}/summary/`,
    TIER_REWARDS_PENDING: '/cp/tier-rewards/pending/',
    BRANCH:               '/cp/branch/',
    BRANCH_STATS:         '/cp/branch/stats/',
    BEHAVIORS:            '/cp/branch/behaviors/',
    BEHAVIORS_MANAGE:     '/cp/branch/behaviors/manage/',
    BEHAVIOR_DETAIL:      (id: number) => `/cp/branch/behaviors/${id}/`,
  },

  // 상담 기록
  NOTES: {
    LIST:   (studentId: number) => `/students/${studentId}/notes/`,
    DETAIL: (noteId: number)    => `/students/notes/${noteId}/`,
  },

  // 출석 관리 시스템
  ATTENDANCE: {
    KIOSK_LOGIN:     '/attendance/kiosk/login/',
    KIOSK_CHECKIN:   '/attendance/kiosk/checkin/',
    RECORDS:         '/attendance/records/',
    TEACHER_MANUAL:  '/attendance/teacher/manual/',
    REGENERATE_CODE: (studentId: number) => `/attendance/students/${studentId}/regenerate-code/`,
    MY_TODAY:        '/attendance/my-today/',
  },

  // 크레딧 시스템
  CREDITS: {
    MY:                  '/credits/my/',
    BALANCE:             (studentId: number) => `/credits/balance/${studentId}/`,
    TRANSACTIONS:        (studentId: number) => `/credits/transactions/${studentId}/`,
    CHARGE_PREPARE:      '/credits/charge/prepare/',
    CHARGE_CONFIRM:      '/credits/charge/confirm/',
    REFUND:              '/credits/refund/',
    ADJUST:              '/credits/adjust/',
    CLASS_PRICES:        '/credits/class-prices/',
    CLASS_PRICES_UPSERT: '/credits/class-prices/upsert/',
    DIRECTOR_LIST:       '/credits/director/list/',
    DIRECTOR_PAYMENTS:   '/credits/director/payments/',
  },

  PAYMENTS: {
    INVOICES:              '/payments/invoices/',
    INVOICE_CANCEL:        (id: number) => `/payments/invoices/${id}/cancel/`,
  },

  // 스마트 자습 시스템
  STUDY: {
    SESSIONS: "/study/sessions/",
    SESSION_START: "/study/sessions/start/",
    SESSION_ACTIVE: "/study/sessions/active/",
    SESSION_COMPLETE: (sessionId: number) => `/study/sessions/${sessionId}/complete/`,
    TODAY: "/study/today/",
    CALENDAR: "/study/calendar/",
    HINT: "/study/hints/",
    HINT4_UNLOCK: "/study/hint4-unlock/",
  },
  NOTIFICATIONS: {
    TIER_PROMOTIONS: "/reports/notifications/tier-promotions/",
    TIER_PROMOTIONS_READ: "/reports/notifications/tier-promotions/read/",
    CP_ACQUISITIONS: "/reports/notifications/cp-acquisitions/",
    CP_ACQUISITIONS_READ: "/reports/notifications/cp-acquisitions/read/",
  },

  // 랜덤 박스
  RANDOMBOX: {
    LIST:     '/randombox/',
    PURCHASE: '/randombox/purchase/',
    OPEN:     (id: number) => `/randombox/open/${id}/`,
    BRANCH:   '/randombox/branch/',
    DELIVER:  (id: number) => `/randombox/${id}/deliver/`,
    BRANCH_ITEMS: '/randombox/branch/items/',
  },

  // 듀오 학습 파트너십
  DUO: {
    STATUS:         '/duo/status/',
    SEARCH:         '/duo/search/',
    INVITE:         '/duo/invite/',
    INVITE_ACCEPT:  '/duo/invite/accept/',
    INVITE_REJECT:  '/duo/invite/reject/',
    DISSOLVE:       '/duo/dissolve/',
    MISSION_CLAIM:  '/duo/missions/claim/',
  },
} as const;

export default API_ENDPOINTS;
